/**
 * WebSocket connection with automatic reconnect.
 *
 * Reconnecting re-sends `hello`, and because identity is a stable id in
 * localStorage the server puts you back where you were. Frames queued while
 * offline are dropped rather than replayed — a stale position is worse than a
 * missing one.
 */

import { PING_INTERVAL_MS, PROTOCOL_VERSION, type ClientMessage, type ServerMessage } from '@virtual-office/shared';

export interface ConnectionHandlers {
  onMessage(message: ServerMessage): void;
  onStatusChange(status: 'connecting' | 'online' | 'offline'): void;
  /** The server refused the upgrade: the session is gone. */
  onUnauthorized(): void;
}

const socketUrl = (): string => {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}/ws`;
};

export class Connection {
  private socket: WebSocket | null = null;
  private pingTimer: number | null = null;
  private reconnectTimer: number | null = null;
  private attempts = 0;
  private closedByUs = false;
  /** Set once the server sends a fatal error, to stop the reconnect loop. */
  private fatal = false;

  constructor(private readonly handlers: ConnectionHandlers) {}

  connect(): void {
    if (this.fatal) return;
    this.closedByUs = false;
    this.handlers.onStatusChange('connecting');

    const socket = new WebSocket(socketUrl());
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempts = 0;
      // Identity came from the session cookie during the handshake; this is
      // only the protocol greeting.
      this.send({ t: 'hello', protocol: PROTOCOL_VERSION });
      this.handlers.onStatusChange('online');
      this.startPings();
    });

    socket.addEventListener('message', (event) => {
      if (typeof event.data !== 'string') return;
      let message: ServerMessage;
      try {
        message = JSON.parse(event.data) as ServerMessage;
      } catch {
        return;
      }
      if (message.t === 'error' && message.fatal) this.fatal = true;
      this.handlers.onMessage(message);
    });

    socket.addEventListener('close', () => {
      this.stopPings();
      this.socket = null;
      if (this.closedByUs || this.fatal) {
        this.handlers.onStatusChange('offline');
        return;
      }
      this.handlers.onStatusChange('offline');
      this.scheduleReconnect();
    });

    socket.addEventListener('error', () => {
      // 'close' always follows; reconnect is handled there.
    });
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    this.attempts += 1;
    const delay = Math.min(500 * 2 ** (this.attempts - 1), 10_000);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private startPings(): void {
    this.stopPings();
    this.pingTimer = window.setInterval(() => {
      this.send({ t: 'ping', ts: Date.now() });
    }, PING_INTERVAL_MS);
  }

  private stopPings(): void {
    if (this.pingTimer !== null) window.clearInterval(this.pingTimer);
    this.pingTimer = null;
  }

  /** Allow reconnecting after a fresh sign-in. */
  resume(): void {
    this.fatal = false;
    this.attempts = 0;
  }

  get online(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  send(message: ClientMessage): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }

  /** Reconnect immediately, e.g. after the tab wakes from sleep. */
  refresh(): void {
    if (this.online || this.fatal) return;
    if (this.reconnectTimer !== null) {
      window.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.attempts = 0;
    this.connect();
  }

  close(): void {
    this.closedByUs = true;
    this.stopPings();
    this.socket?.close(1000, 'client-left');
    this.socket = null;
  }
}
